import type { Frame } from './events'

/**
 * One thing that happened, in the order it happened.
 *
 * <p><b>THE CONVERSATION AND THE GATES ARE ONE SEQUENCE, NOT TWO LISTS.</b> A gate card belongs
 * where it was raised — under the turn that raised it and above the turn that followed — and two
 * parallel arrays cannot say where that was. `Frontend/engagement.html` renders exactly this
 * interleaving, and it is the difference between a transcript and a list of cards.</p>
 */
export type Entry =
  | { at: number; kind: 'phase'; number: number; name: string; entryOrdinal: number }
  | { at: number; kind: 'prose'; text: string; phase: number | null }
  | { at: number; kind: 'gate'; frame: Frame; answer: GateAnswered | null }
  | { at: number; kind: 'note'; frame: Frame }

/** What a person decided, once they have. */
export interface GateAnswered {
  decision: string
  note: string | null
  at: string | null

  /**
   * ONE ENTRY PER QUESTION on a clarify card: what was asked, and what was SAID.
   *
   * <p>The answered card rendered every question beside ALL of its options, which is what was
   * ASKED. A reader looking at a card they answered ten minutes ago could not tell which of four
   * options they had picked, and four questions' options ran together into one block of text.</p>
   */
  answers: { question: string; header: string; answer: string[] }[] | null

  /** A reply that answered no specific question — `§5.1`'s "Reply instead…". */
  response: string | null
}

/**
 * The client's view of one engagement, folded from its event stream.
 *
 * <p>THE REDUCER IS WHERE `seq` EARNS ITS KEEP. M-2 makes it gapless and monotonic per engagement,
 * which is what lets a reconnecting client say "everything after 41" and know that what comes back
 * is everything. A duplicate is dropped here; a GAP IS RECORDED rather than smoothed over, because
 * a reader looking at a partial stream with no indication is being lied to about what happened.</p>
 */
export interface EngagementView {
  lifecycle: string
  activity: string
  phase: number | null
  phaseName: string | null
  entryOrdinal: number | null
  lastSeq: number

  /** The open gate, when there is one. This is what the page renders a card from. */
  gate: Frame | null

  /** Every gate seen, newest last — a specialist may raise several in one turn. */
  gates: Frame[]

  /** The stream in order: turns, gates and the notes between them. */
  timeline: Entry[]

  /**
   * Every phase the run has ENTERED, by number. The rail shows all twelve regardless (`AC-UI-6`);
   * this says which of them have happened and how many times.
   */
  visits: Record<number, { name: string; entryOrdinal: number }>

  /**
   * What each gate kind came to, by kind. The rail's `.attach` rows read it: a gate with an outcome
   * fired, a gate with none was never asked, and those are different facts about the run.
   */
  gateOutcomes: Record<string, string>

  /** When the open gate opened, so the rail can say "open 2 days" rather than "awaiting input". */
  openedAt: string | null

  /** The open gate's kind, which `04 §3.3.3` MUSTs over a generic label. */
  openGateKind: string | null

  /** One line, the latest. Rate-limited to 1/second/engagement at the source. */
  progress: string | null

  /** Prose deltas, coalesced into blocks. */
  conversation: string[]

  /** Specification keys committed, in order. */
  spec: string[]
  results: Frame[]
  flags: Frame[]
  documents: Frame[]
  frozen: Frame | null
  report: Frame | null
  terminal: Frame | null
  error: Frame | null

  /**
   * True when the server said the replay could not reach back far enough (`replay_incomplete`).
   * THE CLIENT MUST NOT SILENTLY RENDER A PARTIAL STREAM (`AC-SSE-5`) — it fetches the state
   * snapshot and rebuilds.
   */
  replayIncomplete: boolean

  /** Sequence numbers that never arrived. Non-empty is a bug, and a visible one. */
  gaps: number[]

  /** Kinds this build does not know. Counted, never thrown on (`AC-SSE-7`). */
  unknownKinds: string[]

  /** What phase 11 published, as the stream reported it. Empty until it does. */
  artefacts: Frame[]
}

export function empty(): EngagementView {
  return {
    lifecycle: 'active', activity: 'working', phase: null, phaseName: null, entryOrdinal: null,
    lastSeq: 0, gate: null, gates: [], timeline: [], visits: {}, gateOutcomes: {},
    openedAt: null, openGateKind: null, progress: null, conversation: [], spec: [], results: [],
    flags: [], documents: [], frozen: null, report: null, terminal: null, error: null,
    replayIncomplete: false, gaps: [], unknownKinds: [], artefacts: [],
  }
}

/** How long a run of token deltas is allowed to grow before a new block is started. */
const BLOCK_LIMIT = 4096

/**
 * Fold one frame in.
 *
 * PURE, AND THAT IS THE POINT: the same sequence of frames always produces the same view, so a
 * reconnect that replays from the log lands on exactly the state a continuous connection would
 * have. A reducer that read the clock or a random value would make replay a different answer from
 * live.
 */
export function reduce(view: EngagementView, frame: Frame): EngagementView {
  const seq = typeof frame.seq === 'number' ? frame.seq : undefined

  // DUPLICATES ARE DROPPED. The subscription opens before the backlog query runs — deliberately,
  // so nothing can be appended into the window between them — which makes an overlap expected.
  if (seq !== undefined && seq <= view.lastSeq) return view

  const next: EngagementView = { ...view }

  if (seq !== undefined) {
    // A GAP IS RECORDED, NOT SMOOTHED. `seq` is gapless by construction, so one here means an
    // event was lost, and the reader is entitled to know that rather than to see a tidy timeline.
    if (view.lastSeq > 0 && seq > view.lastSeq + 1) {
      next.gaps = [...view.gaps]
      for (let missing = view.lastSeq + 1; missing < seq; missing++) next.gaps.push(missing)
    }
    next.lastSeq = seq
  }

  switch (frame.kind) {
    case 'state':
      next.lifecycle = str(frame.lifecycle) ?? view.lifecycle
      next.activity = str(frame.activity) ?? view.activity

      // A STATE THAT IS NOT AWAITING INPUT CLOSES THE CARD. Two tabs on one engagement is not
      // exotic; the second gets a 409 and re-renders from the `state` event that follows, and a
      // card left on screen would invite the same answer again (`AC-SSE-8`).
      if (next.activity !== 'awaiting-input') next.gate = null

      // WHICH GATE, AND SINCE WHEN. A frame that carries the field CLEARS it when it is null —
      // that is how an answered gate stops being the open one — and a frame that does not mention
      // it leaves it alone.
      if ('openGateKind' in frame) next.openGateKind = str(frame.openGateKind)
      if ('openGateOpenedAt' in frame) next.openedAt = str(frame.openGateOpenedAt)
      if ('phase' in frame) next.phase = num(frame.phase) ?? view.phase
      if ('phaseName' in frame) next.phaseName = str(frame.phaseName) ?? view.phaseName
      break

    case 'phase':
      next.phase = num(frame.number) ?? view.phase
      next.phaseName = str(frame.name) ?? view.phaseName
      next.entryOrdinal = num(frame.entryOrdinal) ?? num(frame.entry_ordinal) ?? view.entryOrdinal

      // A PHASE ENTRY ENDS THE PREVIOUS BLOCK of prose. Otherwise phase 4's conversation runs into
      // phase 6's under one heading, and the reader cannot tell which specialist said what.
      if (view.conversation.length > 0 && last(view.conversation) !== '') {
        next.conversation = [...view.conversation, '']
      }

      if (next.phase !== null) {
        next.visits = {
          ...view.visits,
          [next.phase]: {
            name: next.phaseName ?? '',
            entryOrdinal: next.entryOrdinal ?? 0,
          },
        }
        next.timeline = [...view.timeline, {
          at: seq ?? view.timeline.length,
          kind: 'phase',
          number: next.phase,
          name: next.phaseName ?? '',
          entryOrdinal: next.entryOrdinal ?? 0,
        }]
      }
      break

    case 'gate': {
      const id = str(frame.gate_id) ?? str(frame.gateId)

      // A SECOND FRAME FOR A GATE ALREADY ON THE PAGE IS ITS ANSWER, not a second question. The
      // card stays where it was raised and gains the decision, because a card that vanished when
      // it was answered would leave the reader scrolling a conversation with its decisions cut out.
      if (str(frame.state) === 'answered') {
        const answer: GateAnswered = {
          decision: str(frame.decision) ?? '',
          note: str(frame.note),
          at: str(frame.at),
          answers: answersOf(frame.answers),
          response: str(frame.response),
        }

        next.timeline = view.timeline.map((entry) =>
          entry.kind === 'gate'
            && (str(entry.frame.gate_id) ?? str(entry.frame.gateId)) === id
            ? { ...entry, answer }
            : entry)

        const kind = str(frame.gate)
        if (kind !== null) {
          next.gateOutcomes = { ...view.gateOutcomes, [kind]: answer.decision }
        }

        if ((str(view.gate?.gate_id) ?? str(view.gate?.gateId)) === id) next.gate = null
        next.activity = 'working'
        next.openGateKind = null
        next.openedAt = null
        break
      }

      next.gate = frame
      next.gates = [...view.gates, frame]
      next.activity = 'awaiting-input'
      next.openGateKind = str(frame.gate)
      next.timeline = [...view.timeline, {
        at: seq ?? view.timeline.length, kind: 'gate', frame, answer: null,
      }]
      break
    }

    case 'progress':
      next.progress = str(frame.text) ?? view.progress
      break

    case 'token': {
      const text = str(frame.text) ?? ''
      if (text.length === 0) break
      const blocks = [...view.conversation]
      const tail = blocks.length > 0 ? blocks[blocks.length - 1] ?? '' : ''
      if (blocks.length === 0 || tail.length + text.length > BLOCK_LIMIT) blocks.push(text)
      else blocks[blocks.length - 1] = tail + text
      next.conversation = blocks

      // THE SAME PROSE, IN PLACE. Deltas coalesce into the last prose entry unless a gate or a
      // phase has been recorded since — in which case they are a new turn, below it.
      const tip = view.timeline[view.timeline.length - 1]
      if (tip !== undefined && tip.kind === 'prose' && tip.text.length + text.length <= BLOCK_LIMIT) {
        next.timeline = [...view.timeline.slice(0, -1),
                         { ...tip, text: tip.text + text }]
      } else {
        next.timeline = [...view.timeline, {
          at: seq ?? view.timeline.length, kind: 'prose', text, phase: view.phase,
        }]
      }
      break
    }

    case 'spec': {
      const key = str(frame.key)
      if (key && !view.spec.includes(key)) next.spec = [...view.spec, key]
      break
    }

    case 'result':
      next.results = [...view.results, frame]
      break

    case 'flag':
      next.flags = [...view.flags, frame]
      break

    case 'document':
      next.documents = [...view.documents, frame]
      break

    case 'frozen':
      next.frozen = frame
      break

    // WHAT PHASE 11 PUBLISHED, CARRIED ON THE STREAM. The artefact card can then appear the moment
    // the report exists rather than after a fetch that only fires on a terminal state — which
    // matters because M-10 puts publication BEFORE the sign gate, so a reader is meant to be able
    // to open the report while deciding whether to sign it.
    case 'artefacts':
      next.artefacts = Array.isArray(frame['artefacts']) ? frame['artefacts'] as Frame[] : []
      break

    case 'error':
      next.error = frame

      // AN ORCHESTRATION FAULT IS TERMINAL, AND THE PAGE HAS TO SAY SO. B-172.
      //
      // This set `error` and nothing else, so the rail went on reporting "Working on parameters"
      // for an engagement that had stopped — while the LIST, which reads the engagement document,
      // correctly showed it as interrupted. Two surfaces, one engagement, opposite claims; the one
      // a reader is watching was the one that was wrong.
      //
      // Only `orchestration_faulted` is terminal. An `error` frame is also how lesser trouble is
      // reported, and treating every one of them as the end would stop a run that is still going.
      if ((frame['error'] as { code?: unknown } | undefined)?.code === 'orchestration_faulted') {
        next.terminal = frame
        next.lifecycle = 'faulted'
        next.activity = 'idle'
        next.gate = null
      }
      break

    case 'halted':
    case 'done':
      next.terminal = frame
      next.lifecycle = frame.kind === 'done' ? 'signed' : 'halted'
      next.activity = 'idle'
      next.gate = null
      break

    case 'replay_incomplete':
      next.replayIncomplete = true
      break

    case 'eligibility':
      // THE FUNNEL IS PART OF THE CONVERSATION, not a panel. "Five matrix-based methods were
      // removed by one fact, not by a judgment" is a thing the reader has to see next to the
      // method choice it explains.
      next.timeline = [...view.timeline, {
        at: seq ?? view.timeline.length, kind: 'note', frame,
      }]
      break

    case 'recycle':
    case 'token_gap':
    case 'run':
    case 'tool':
    case 'rate_limit':
      // Known and not rendered by this view. Named rather than defaulted, so a reader can see the
      // difference between "handled elsewhere" and "nobody thought about it".
      break

    default:
      // AN UNKNOWN KIND IS IGNORED, NEVER THROWN ON (`AC-SSE-7`). It is counted, because a client
      // silently discarding a third of a new event type should be discoverable.
      if (!view.unknownKinds.includes(frame.kind)) {
        next.unknownKinds = [...view.unknownKinds, frame.kind]
      }
      break
  }

  return next
}

/** Fold a whole sequence, for a replay. */
export function reduceAll(frames: readonly Frame[], from: EngagementView = empty()): EngagementView {
  return frames.reduce(reduce, from)
}

/**
 * A clarify card's answers, IN THE ONE SHAPE THE PAGE RENDERS — `answer` is always `string[]`.
 *
 * <p>The frame carries whatever the caller posted: this SPA sends an array, but every clarify
 * answered from 6 Oct onward carries `answer` as a bare STRING. Cast blindly, `"2024".join` threw
 * inside the answered card and, with no boundary above it, React unmounted the whole engagement —
 * the page painted and then went blank (84 of 157 engagements). Normalised here, at the one place
 * frames come in, so no card has to know there were ever two shapes.</p>
 */
function answersOf(value: unknown): GateAnswered['answers'] {
  if (!Array.isArray(value)) return null

  return value.flatMap((raw) => {
    if (typeof raw !== 'object' || raw === null) return []
    const entry = raw as Record<string, unknown>
    const said = entry['answer']

    return [{
      question: str(entry['question']) ?? '',
      header: str(entry['header']) ?? '',
      answer: typeof said === 'string' ? (said.length > 0 ? [said] : [])
        : Array.isArray(said) ? said.filter((a): a is string => typeof a === 'string')
        : [],
    }]
  })
}

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function last(items: readonly string[]): string | undefined {
  return items[items.length - 1]
}
